import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import * as actions from './index';
import { HubService } from './services/hub.service';
import { QueryResponse } from '../shared/models/query-response';
import { UserSettings } from './services/models/userData.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CountryList } from './services/countriesList.model';

@Injectable()
export class HubEffects {
  private actions$ = inject(Actions);
  private api = inject(HubService);

  private _snackBar = inject(MatSnackBar);

  getUserData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.HubActions.appInit, actions.HubActions.updateUserSettingsSuccess),
      switchMap(() =>
        this.api.getUserData().pipe(
          map((response: QueryResponse<UserSettings>) => {
            if (response.isSuccess) {
              return actions.HubActions.userDataSuccess({
                result: response.result
              });
            } else {
              return actions.HubActions.userDataFailure({
                error: response.errors as string
              });
            }
          }),
          catchError((error) => {
            return of(
              actions.HubActions.userDataFailure({
                error: error.statusText || 'An unknown error occurred'
              })
            );
          })
        )
      )
    )
  );

  getCountriesList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.HubActions.appInit),
      switchMap(() =>
        this.api.getCountriesList().pipe(
          map((response: QueryResponse<CountryList[]>) => {
            if (response.isSuccess) {
              return actions.HubActions.countryListSuccess({
                result: response.result
              });
            } else {
              return actions.HubActions.countryListFailure({
                error: response.errors as string
              });
            }
          })
        )
      )
    )
  );

  updateUserSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.HubActions.updateUserSettings),
      switchMap((action) =>
        this.api.updateUserSettings(action.settings).pipe(
          map((response) => {
            if (response.isSuccess) {
              this.openSnackBar('User settings updated successfully.');
              return actions.HubActions.updateUserSettingsSuccess();
            } else {
              this.openSnackBar('Failed to update user settings, Please try again.');
              return actions.HubActions.updateUserSettingsFailure({
                error: response.errors.toString()
              });
            }
          }),
          catchError((error) => {
            this.openSnackBar('Failed to update user settings, Please try again.');
            return of(
              actions.HubActions.updateUserSettingsFailure({
                error: error.message || 'An unknown error occurred'
              })
            );
          })
        )
      )
    )
  );

  openSnackBar(message: string) {
    this._snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'right'
    });
  }
}
